import { useState, useEffect } from 'react'
import { ChevronDown, Check, User } from 'lucide-react'

interface Idea {
  id: string
  title: string
  content: {
    concept?: string
    theme?: string
    integration_concept?: string
    talking_points?: string
  }
  createdBy: string
  createdByTeam?: string | null
  createdAt: string
}

interface SuggestionSheetProps {
  ideas: Idea[]
  onUseSuggestion: (concept: string) => void
  onClose: () => void
}

function resolveConceptText(idea: Idea): string {
  if (idea.content.concept) return idea.content.concept
  return [idea.content.theme, idea.content.integration_concept, idea.content.talking_points]
    .filter(Boolean)
    .join('\n\n')
}

const SuggestionSheet = ({ ideas, onUseSuggestion, onClose }: SuggestionSheetProps) => {
  const [isVisible, setIsVisible] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [used, setUsed] = useState(false)

  const current = ideas[currentIndex]
  const hasMultiple = ideas.length > 1

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsVisible(true))
    document.body.style.overflow = 'hidden'
    return () => {
      cancelAnimationFrame(frame)
      document.body.style.overflow = ''
    }
  }, [])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(onClose, 280)
  }

  const handleUse = () => {
    onUseSuggestion(resolveConceptText(current))
    setUsed(true)
    setTimeout(handleClose, 600)
  }

  if (!current) return null

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div
        className={`absolute inset-0 bg-black/60 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />

      <div
        className={`relative flex flex-col bg-[#0f1623] rounded-t-3xl transition-transform duration-300 ease-out ${isVisible ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ maxHeight: '80dvh' }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/8 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
              <User className="w-4 h-4 text-gray-300" />
            </div>
            <div>
              <p className="text-white text-sm font-semibold leading-tight">
                {hasMultiple
                  ? `Suggestion ${currentIndex + 1} of ${ideas.length}`
                  : 'Concept Suggestion'}
              </p>
              <p className="text-gray-500 text-xs">
                {current.createdByTeam
                  ? `The team at ${current.createdByTeam}`
                  : `From ${current.createdBy}`}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-white transition-colors rounded-full hover:bg-white/10"
          >
            <ChevronDown className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-5 overscroll-contain">
          {current.title && (
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-3">{current.title}</p>
          )}
          <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-4">
            <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">
              {resolveConceptText(current) || 'No concept text provided.'}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 px-5 pb-8 pt-4 border-t border-white/8 space-y-2.5">
          {hasMultiple && (
            <div className="flex gap-2 mb-1">
              <button
                onClick={() => setCurrentIndex((i) => (i > 0 ? i - 1 : ideas.length - 1))}
                className="flex-1 py-2.5 text-sm text-gray-400 hover:text-white border border-white/10 hover:border-white/20 rounded-xl transition-colors"
              >
                ← Previous
              </button>
              <button
                onClick={() => setCurrentIndex((i) => (i < ideas.length - 1 ? i + 1 : 0))}
                className="flex-1 py-2.5 text-sm text-gray-400 hover:text-white border border-white/10 hover:border-white/20 rounded-xl transition-colors"
              >
                Next →
              </button>
            </div>
          )}
          <button
            onClick={handleUse}
            disabled={used}
            className={`w-full py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
              used
                ? 'bg-white/10 text-gray-400 cursor-default'
                : 'bg-[#C0FD72] text-black hover:bg-[#b0ef62]'
            }`}
          >
            {used ? (
              <>
                <Check className="w-4 h-4" />
                Applied to your concept
              </>
            ) : (
              'Use this concept \u2192'
            )}
          </button>
          <button
            onClick={handleClose}
            className="w-full py-3 text-sm text-gray-500 hover:text-gray-300 transition-colors"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  )
}

export default SuggestionSheet
